import type { SVGProps } from 'react';

/**
 * The hand-off's own icons, traced from its exports rather than matched to lucide glyphs: these are
 * vuesax line icons and the nearest lucide equivalents are visibly different drawings.
 *
 * Every stroke is `currentColor`, so one component serves both surfaces the page uses them on — white
 * on the dark panel, ink on the pale address card — where the exports carry a baked-in colour each.
 * Drawn on a 16x16 grid, which is the size the hand-off places them at.
 *
 * One deviation: the call glyph ships its 50% dimming INSIDE the path while the location one has it
 * on the wrapper. Both are stripped here so the markup dims them, and nothing renders differently.
 */

/** A building — the Empresa audience. */
export function BuildingIcon(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
			<path
				d="M8.33331 14.6667H2.71998C1.94665 14.6667 1.31331 14.0467 1.31331 13.2867V3.3934C1.31331 1.64674 2.61331 0.853403 4.20665 1.6334L7.16665 3.08674C7.80665 3.40007 8.33331 4.2334 8.33331 4.94007V14.6667Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M14.6466 10.0401V12.5601C14.6466 14.0001 13.98 14.6667 12.54 14.6667H8.33331V6.94674L8.64665 7.0134L11.6466 7.68674L13 7.98674C13.88 8.18007 14.6 8.6334 14.64 9.9134C14.6466 9.9534 14.6466 9.9934 14.6466 10.0401Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path d="M3.66665 6.00007H5.97998" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
			<path d="M3.66665 8.66674H5.97998" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
			<path
				d="M11.6466 7.68674V9.8334C11.6466 10.6601 10.9733 11.3334 10.1466 11.3334C9.31998 11.3334 8.64665 10.6601 8.64665 9.8334V7.0134L11.6466 7.68674Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M14.64 9.9134C14.6 10.7001 13.9466 11.3334 13.1466 11.3334C12.32 11.3334 11.6466 10.6601 11.6466 9.8334V7.68674L13 7.98674C13.88 8.18007 14.6 8.6334 14.64 9.9134Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

/** A heart — the NGO / social entity audience. */
export function HeartIcon(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
			<path
				d="M8.41333 13.8733C8.18667 13.9533 7.81333 13.9533 7.58667 13.8733C5.65333 13.2133 1.33333 10.46 1.33333 5.79333C1.33333 3.73333 2.99333 2.06667 5.04 2.06667C6.25333 2.06667 7.32667 2.65333 8 3.56C8.67333 2.65333 9.75333 2.06667 10.96 2.06667C13.0067 2.06667 14.6667 3.73333 14.6667 5.79333C14.6667 10.46 10.3467 13.2133 8.41333 13.8733Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

/** Two people — the Donor audience. */
export function PeopleIcon(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
			<path
				d="M12 4.77334C11.96 4.76667 11.9133 4.76667 11.8733 4.77334C10.9533 4.74 10.22 3.98667 10.22 3.05333C10.22 2.1 10.9867 1.33333 11.94 1.33333C12.8933 1.33333 13.66 2.10667 13.66 3.05333C13.6533 3.98667 12.92 4.74 12 4.77334Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M11.3133 9.62667C12.2267 9.78001 13.2333 9.62001 13.94 9.14667C14.88 8.52001 14.88 7.49334 13.94 6.86667C13.2267 6.39334 12.2067 6.23334 11.2933 6.39334"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M3.98 4.77334C4.02 4.76667 4.06667 4.76667 4.10667 4.77334C5.02667 4.74 5.76 3.98667 5.76 3.05333C5.76 2.1 4.99333 1.33333 4.04 1.33333C3.08667 1.33333 2.32 2.10667 2.32 3.05333C2.32667 3.98667 3.06 4.74 3.98 4.77334Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4.66667 9.62667C3.75333 9.78001 2.74667 9.62001 2.04 9.14667C1.1 8.52001 1.1 7.49334 2.04 6.86667C2.75333 6.39334 3.77333 6.23334 4.68667 6.39334"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M8 9.75334C7.96 9.74667 7.91333 9.74667 7.87333 9.75334C6.95333 9.72 6.22 8.96667 6.22 8.03334C6.22 7.08 6.98667 6.31333 7.94 6.31333C8.89333 6.31333 9.66 7.08667 9.66 8.03334C9.65333 8.96667 8.92 9.72667 8 9.75334Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M6.06 11.8533C5.12 12.48 5.12 13.5067 6.06 14.1333C7.12667 14.8467 8.87333 14.8467 9.94 14.1333C10.88 13.5067 10.88 12.48 9.94 11.8533C8.88 11.1467 7.12667 11.1467 6.06 11.8533Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

/** A microphone — the Press audience. */
export function MicrophoneIcon(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
			<path
				d="M8 10.3333C9.47333 10.3333 10.6667 9.14 10.6667 7.66667V4C10.6667 2.52667 9.47333 1.33333 8 1.33333C6.52667 1.33333 5.33333 2.52667 5.33333 4V7.66667C5.33333 9.14 6.52667 10.3333 8 10.3333Z"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2.9 6.43333V7.56667C2.9 10.38 5.18667 12.6667 8 12.6667C10.8133 12.6667 13.1 10.38 13.1 7.56667V6.43333"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7.07333 4.28667C7.67333 4.06667 8.32667 4.06667 8.92667 4.28667"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7.46667 5.7C7.82 5.60667 8.18667 5.60667 8.54 5.7"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path d="M8 12.6667V14.6667" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

/** A handset — the phone line on the address card. */
export function CallIcon(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
			<path
				d="M14.6467 12.22C14.6467 12.46 14.5933 12.7067 14.48 12.9467C14.3667 13.1867 14.22 13.4133 14.0267 13.6267C13.7 13.9867 13.34 14.2467 12.9333 14.4133C12.5333 14.58 12.1 14.6667 11.6333 14.6667C10.9533 14.6667 10.2267 14.5067 9.46 14.18C8.69333 13.8533 7.92667 13.4133 7.16667 12.86C6.4 12.3 5.67333 11.68 4.98 10.9933C4.29333 10.3 3.67333 9.57333 3.12 8.81333C2.57333 8.05333 2.13333 7.29333 1.81333 6.54C1.49333 5.78 1.33333 5.05333 1.33333 4.36C1.33333 3.90667 1.41333 3.47333 1.57333 3.07333C1.73333 2.66667 1.98667 2.29333 2.34 1.96C2.76667 1.54 3.23333 1.33333 3.72667 1.33333C3.91333 1.33333 4.1 1.37333 4.26667 1.45333C4.44 1.53333 4.59333 1.65333 4.71333 1.82667L6.26 4.00667C6.38 4.17333 6.46667 4.32667 6.52667 4.47333C6.58667 4.61333 6.62 4.75333 6.62 4.88C6.62 5.04 6.57333 5.2 6.48 5.35333C6.39333 5.50667 6.26667 5.66667 6.10667 5.82667L5.6 6.35333C5.52667 6.42667 5.49333 6.51333 5.49333 6.62C5.49333 6.67333 5.5 6.72 5.51333 6.77333C5.53333 6.82667 5.55333 6.86667 5.56667 6.90667C5.68667 7.12667 5.89333 7.41333 6.18667 7.76C6.48667 8.10667 6.80667 8.46 7.15333 8.81333C7.51333 9.16667 7.86 9.49333 8.21333 9.79333C8.56 10.0867 8.84667 10.2867 9.07333 10.4067C9.10667 10.42 9.14667 10.44 9.19333 10.46C9.24667 10.48 9.3 10.4867 9.36 10.4867C9.47333 10.4867 9.56 10.4467 9.63333 10.3733L10.14 9.87333C10.3067 9.70667 10.4667 9.58 10.62 9.5C10.7733 9.40667 10.9267 9.36 11.0933 9.36C11.22 9.36 11.3533 9.38667 11.5 9.44667C11.6467 9.50667 11.8 9.59333 11.9667 9.70667L14.1733 11.2733C14.3467 11.3933 14.4667 11.5333 14.54 11.7C14.6067 11.8667 14.6467 12.0333 14.6467 12.22Z"
				stroke="currentColor"
			/>
		</svg>
	);
}

/** A map pin — the office address. */
export function LocationIcon(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
			<path
				d="M8 8.95333C9.14875 8.95333 10.08 8.02209 10.08 6.87333C10.08 5.72458 9.14875 4.79333 8 4.79333C6.85125 4.79333 5.92 5.72458 5.92 6.87333C5.92 8.02209 6.85125 8.95333 8 8.95333Z"
				stroke="currentColor"
			/>
			<path
				d="M2.41333 5.66C3.72667 -0.113333 12.28 -0.106666 13.5867 5.66667C14.3533 9.05333 12.2467 11.92 10.4 13.6933C9.06 14.9867 6.94 14.9867 5.59333 13.6933C3.75333 11.92 1.64667 9.04667 2.41333 5.66Z"
				stroke="currentColor"
			/>
		</svg>
	);
}
